"use client";

import {useEffect, useRef, useState} from "react";
import {clsx} from "clsx";
import {LegalListItem} from "@/types/legal";

/** Share of the rail the thumb occupies, matching the 103px thumb in the design. */
const THUMB_RATIO = 0.21;

/**
 * Contents card from the design: a white panel listing every clause, with the
 * current one set in semibold and a 4px rail on the right tracking progress
 * through the document.
 */
export default function TableOfContents({
                                            sections,
                                            label,
                                        }: {
    sections: LegalListItem[];
    label: string;
}) {
    const [activeSlug, setActiveSlug] = useState(sections[0]?.slug ?? "");
    const [progress, setProgress] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const headings = sections
            .map((section) => document.getElementById(section.slug))
            .filter((el): el is HTMLElement => el !== null);

        if (!headings.length) return;

        // Every one of these reads forces a layout, so the document positions are
        // measured once per resize and the scroll handler works off the cache.
        let tops: number[] = [];
        let first = 0;
        let span = 0;

        const measure = () => {
            tops = headings.map((heading) => heading.getBoundingClientRect().top + window.scrollY);
            first = tops[0];
            const last = headings[headings.length - 1];
            const end = last.getBoundingClientRect().bottom + window.scrollY;
            span = end - first - window.innerHeight;
        };

        let raf = 0;

        const read = () => {
            raf = 0;

            // The clause whose top has most recently passed the reading line.
            const readingLine = window.scrollY + window.innerHeight * 0.3;
            let current = 0;

            for (let i = 0; i < tops.length; i++) {
                if (tops[i] <= readingLine) {
                    current = i;
                } else {
                    break;
                }
            }

            setActiveSlug(headings[current].id);
            setProgress(
                span <= 0 ? 1 : Math.min(1, Math.max(0, (window.scrollY - first) / span)),
            );
        };

        const update = () => {
            if (!raf) raf = requestAnimationFrame(read);
        };

        const onResize = () => {
            measure();
            update();
        };

        measure();
        read();

        window.addEventListener("scroll", update, {passive: true});
        window.addEventListener("resize", onResize);

        // Clauses reflow as fonts and embedded media settle, which moves every
        // cached offset — re-measure when the document actually changes height.
        const observer = new ResizeObserver(onResize);
        observer.observe(document.documentElement);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", onResize);
            observer.disconnect();
        };
    }, [sections]);

    // Keep the active entry in view when the list is taller than its panel.
    useEffect(() => {
        const list = listRef.current;
        const active = list?.querySelector<HTMLAnchorElement>(`a[href="#${activeSlug}"]`);
        if (!list || !active) return;

        const listBox = list.getBoundingClientRect();
        const activeBox = active.getBoundingClientRect();

        if (activeBox.top < listBox.top || activeBox.bottom > listBox.bottom) {
            list.scrollTop += activeBox.top - listBox.top - listBox.height / 2 + activeBox.height / 2;
        }
    }, [activeSlug]);

    return (
        <nav
            aria-label={label}
            className="bg-white w-full lg:w-[301px] lg:shrink-0 lg:sticky lg:top-[7.5rem] flex flex-col gap-4 md:gap-5 ps-5 pe-3 md:ps-6 py-6 md:py-8"
        >
            <p className="font-heading capitalize text-xl md:text-2xl leading-[1.3] text-brandColor">
                {label}
            </p>

            <div className="flex gap-4 md:gap-5 items-start">
                <div
                    ref={listRef}
                    className="flex-1 min-w-0 flex flex-col gap-3 text-base leading-[1.4] lg:max-h-[calc(100svh-16rem)] lg:overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {sections.map((section, index) => {
                        const isActive = section.slug === activeSlug;

                        return (
                            <a
                                key={section.id}
                                href={`#${section.slug}`}
                                aria-current={isActive ? "true" : undefined}
                                className={clsx(
                                    "text-brandColor transition-colors duration-300 hover:text-primary",
                                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                                    isActive ? "font-semibold" : "font-normal",
                                )}
                            >
                                {index + 1}. {section.itemTitle}
                            </a>
                        );
                    })}
                </div>

                <div
                    aria-hidden
                    className="hidden lg:block relative w-[4px] self-stretch rounded-[12px] bg-[#d9d9d9]/40"
                >
                    <div
                        className="absolute inset-x-0 rounded-[12px] bg-[#d9d9d9] transition-[top] duration-200 ease-out motion-reduce:transition-none"
                        style={{
                            height: `${THUMB_RATIO * 100}%`,
                            top: `${progress * (1 - THUMB_RATIO) * 100}%`,
                        }}
                    />
                </div>
            </div>
        </nav>
    );
}
